/**
 * Endpoints `client-service` serves in PLAINTEXT — the client-side mirror of the
 * backend's `@PlaintextPayload` opt-outs.
 *
 * Source of truth: `client-service` @ `feature/generic-payload-encryption` (32b5bd0).
 * The two `@ControllerAdvice` beans in `com.fispoke.application.crypto` encrypt
 * every endpoint by default; an endpoint is excluded when it (or its controller)
 * carries `@PlaintextPayload`, or when its declared return type is binary
 * (`byte[]` / `Resource`, including inside a `ResponseEntity`).
 *
 * The backend's rule is structural — a Java annotation and a return type — and the
 * browser can see neither, so this list has to be carried as literal data. That
 * makes it the part of this feature that rots: a new `@PlaintextPayload` on the
 * backend is a silent production break here.
 *
 * KEEP IN SYNC. When the backend adds or removes an exception, update this file in
 * the same PR. Backend coverage as transcribed: 524 encrypted / 60 plaintext.
 */

/**
 * Whole controllers marked `@PlaintextPayload` at class level. Matched as prefixes,
 * so every current and future route under them is plaintext too — which is exactly
 * the backend's semantics for a class-level annotation.
 */
export const PLAINTEXT_PREFIXES: readonly string[] = [
  // AdvyzonController
  '/advyzon/',
  // AdvyzonExchangeController + AdvyzonSamlController
  '/api/sso/advyzon',
  // BenchmarksController
  '/benchmarks',
  // DocumentController — all document I/O, incl. every multipart upload the SPA sends
  '/api/documents/',
  // HealthController (the bare `/` route is handled in PLAINTEXT_EXACT)
  '/health/',
  // LogMaintenanceController
  '/mylogger/',
  // OrionController
  '/orion/',
  // PartnerPortfolioProjectorController
  '/api/partners/portfolioprojector/',
  // SendGridWebhookController
  '/sendgrid/',
  // SFTPController
  '/sftp/',
  // WealthBoxController — no trailing slash: `/api/wealthbox` itself is a route
  '/api/wealthbox',
  // NavigatorWebhookController + WebHookController — both fully plaintext
  '/api/webhooks/',
];

/**
 * Handlers marked `@PlaintextPayload` at method level. Their sibling routes on the
 * same controller ARE encrypted, so these must match exactly — never as prefixes.
 *
 * Note `/api/helix/*` and `/public/*`: most of those controllers is encrypted, only
 * these specific handlers opted out.
 */
export const PLAINTEXT_EXACT: readonly string[] = [
  // AccountsController / ClientController / PublicController — health probes
  '/api/accounts/hello',
  '/api/clients/hello',
  '/public/hello',
  // AuthController
  '/api/auth-service/health',
  // HealthController root
  '/',
  // EmailController
  '/api/email/send_email',
  // PublicController
  '/public/uploadDocTest',
  '/public/download_files_zip',
  '/public/decrypt_payload',
];

/**
 * Method-level opt-outs whose routes carry path variables, as regexes over the
 * request path. All six are HelixController GETs.
 */
export const PLAINTEXT_PATTERNS: readonly RegExp[] = [
  /^\/api\/helix\/get_document_list\/[^/]+$/,
  /^\/api\/helix\/account-info\/[^/]+\/[^/]+$/,
  /^\/api\/helix\/accounts\/[^/]+$/,
  /^\/api\/helix\/statement_list\/[^/]+$/,
  /^\/api\/helix\/statement_download\/[^/]+\/[^/]+$/,
  /^\/api\/helix\/get_transaction\/[^/]+\/[^/]+$/,
];

/**
 * Endpoints that encrypt their RESPONSE but take no `@RequestBody`.
 *
 * `PayloadDecryptionAdvice` is a `RequestBodyAdvice`, so it never fires for these —
 * Spring simply doesn't read the body. Wrapping it in `{ encryptedData }` would make
 * the payload vanish silently behind a 200.
 *
 * The backend reports 20 such endpoints; the GETs among them are harmless (no body
 * to wrap) and are covered by the "no body" guard in `crypto-policy.ts`. Only the
 * POSTs need listing here.
 *
 * INCOMPLETE — seeded from the two POSTs identified so far. Complete it from the
 * "Resp only" filter of the backend coverage report. Neither currently exists in the
 * generated client (`api-docs.json` is ~200 operations behind the backend), so this
 * list is inert until the client is regenerated against `cs-svc-enc`.
 */
export const REQUEST_PLAINTEXT_EXACT: readonly string[] = [
  '/api/sbl/save_custodian_accounts',
  '/api/sbl/save_preeligible_custodian_accounts',
];

/** True when `path` is served in plaintext in both directions. */
export function isPlaintextPath(path: string): boolean {
  if (PLAINTEXT_EXACT.includes(path)) {
    return true;
  }
  if (PLAINTEXT_PREFIXES.some((prefix) => path.startsWith(prefix))) {
    return true;
  }
  return PLAINTEXT_PATTERNS.some((pattern) => pattern.test(path));
}

/** True when `path` encrypts its response but takes no `@RequestBody`. */
export function isRequestPlaintextPath(path: string): boolean {
  return REQUEST_PLAINTEXT_EXACT.includes(path);
}
